import { startTransition, useEffect, useState } from "react";

import { useFormContext } from "react-hook-form";

import {
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Form from "../base/Form/Form";
import { Alert } from "../base/Alert";
import { Button } from "../base/Button";
import LoadingWheel from "../shared/LoadingWheel";
import { FormErrors } from "../base/Form/FormErrors";
import { errorResolver } from "../../utils/errorResolver";
import { FormTextField } from "../base/Form/FormTextField";
import { useClipboardCopy } from "../../hooks/useClipboardCopy";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useApplicationSettings from "../../data/ApplicationSettings/useApplicationSettings";
import useApplicationSettingsMutations from "../../data/ApplicationSettings/useApplicationSettingsMutations";
import {
  CreateUpdateApplicationSetting,
  IApplicationSetting,
} from "../../models/ApplicationSettings/ApplicationSettings";
import clsx from "clsx";
import { alphabeticalSort } from "./../../utils/alphabeticalSort";
import { initializeApp } from "firebase/app";

interface ApplicationSettingsListEditRowProps {
  index: number;
  setting?: IApplicationSetting;
}

function ApplicationSettingsListEditRow({
  index,
  setting,
}: ApplicationSettingsListEditRowProps) {
  const { register } = useFormContext();

  return (
    <div className="w-full py-4 flex items-center space-x-12" key={index}>
      <FormTextField
        {...register(`${index}.name`, {
          value: setting?.name,
        })}
        disabled={setting !== undefined}
        className="w-56"
      />
      <FormTextField
        fullWidth
        {...register(`${index}.value`, { value: setting?.value })}
        containerClassName="flex-grow"
      />
      <input
        {...register(`${index}.id`, { value: setting?.id })}
        type="hidden"
        className="invisible hidden"
      />
    </div>
  );
}

interface ApplicationSettingsListRowProps {
  index: number;
  setting: IApplicationSetting;
  initialShow: boolean;
  onDelete: (settingId: string) => void;
}

export function ApplicationSettingsListRow({
  index,
  setting,
  initialShow,
  onDelete,
}: ApplicationSettingsListRowProps) {
  const { CopyToClipboardButton } = useClipboardCopy();
  const [show, setShow] = useState(initialShow);

  useEffect(() => {
    startTransition(() => {
      setShow(initialShow);
    });
  }, [initialShow]);

  return (
    <div
      className={clsx(
        "px-4 py-4 grid grid-cols-12 gap-x-4",
        !setting.value && "bg-red-50"
      )}
      key={index}
    >
      <span className="col-span-2 text-sm font-medium">{setting.name}</span>
      {show ? (
        <span className="col-span-9 text-sm text-right font-mono text-gray-800 break-all">
          {setting.value ? (
            setting.value
          ) : (
            <span className="italic">{"<no-value>"}</span>
          )}
        </span>
      ) : (
        <span className="col-span-9" />
      )}
      <div className="col-span-1 flex items-center justify-end space-x-2">
        <Button size="xs" onClick={() => setShow((show) => !show)}>
          {show ? (
            <EyeSlashIcon className="w-4 h-4" />
          ) : (
            <EyeIcon className="w-4 h-4" />
          )}
        </Button>
        <CopyToClipboardButton copyText={setting.value} />
        <Button
          size="xs"
          className="bg-red-500 border-red-600 hover:bg-red-600 hover:border-red-600 active:bg-red-500 active:border-red-500"
          onClick={() => onDelete(setting.id)}
        >
          <TrashIcon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

type ApplicationSettingsListProps =
  | {
      ids: {
        applicationId: string;
        applicationRegistrationId?: never;
      };
      editing: boolean;
      onComplete: (success: boolean) => void;
      onDelete: (settingId: string) => void;
      showAll: boolean;
    }
  | {
      ids: {
        applicationId?: never;
        applicationRegistrationId: string;
      };
      editing: boolean;
      onComplete: (success: boolean) => void;
      onDelete: (settingId: string) => void;
      showAll: boolean;
    };

export function ApplicationSettingsList({
  ids,
  editing,
  onComplete,
  onDelete,
  showAll,
}: ApplicationSettingsListProps) {
  const { saveHandlers } = useDefaultCRUDHandlers("Application settings");
  const { applicationSettings } = useApplicationSettings(ids);
  const { create, update } = useApplicationSettingsMutations(ids);
  const [newSettings, setNewSettings] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (data: CreateUpdateApplicationSetting[]) => {
    setSubmitting(true);

    let putOperations: Promise<CreateUpdateApplicationSetting>[] = [];
    let postOperations: Promise<CreateUpdateApplicationSetting>[] = [];

    Object.values(data).forEach((setting) => {
      if (setting.id) {
        putOperations.push(
          update.mutateAsync({
            ...setting,
            applicationOrRegistrationId:
              ids.applicationId || ids.applicationRegistrationId,
          })
        );
      } else {
        postOperations.push(
          create.mutateAsync({
            ...setting,
            applicationOrRegistrationId:
              ids.applicationId || ids.applicationRegistrationId,
          })
        );
      }
    });

    Promise.all([...postOperations, ...putOperations])
      .then(() => {
        saveHandlers.onSuccess();
        onComplete(true);
      })
      .catch(() => {
        saveHandlers.onError();
        onComplete(false);
      })
      .finally(() => setSubmitting(false));
  };

  if (applicationSettings.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingWheel />
      </div>
    );
  } else if (applicationSettings.isError || !applicationSettings.data) {
    return (
      <Alert
        severity="error"
        title="Error getting application settings"
        message={errorResolver(applicationSettings.error)}
      />
    );
  } else if (editing) {
    return submitting ? (
      <div className="py-10 flex items-center justify-center">
        <LoadingWheel />
      </div>
    ) : (
      <>
        <div className="p-4">
          <p className="text-gray-700 text-sm italic">
            If you choose to copy a setting to all instances, only the key will
            get copied
          </p>
        </div>
        <Form<CreateUpdateApplicationSetting[]>
          defaultValues={applicationSettings.data}
          onSubmit={handleSubmit}
        >
          {() => (
            <>
              <FormErrors />
              <div className="px-4 divide-y divide-gray-100">
                {applicationSettings.data?.map((setting, i) => (
                  <ApplicationSettingsListEditRow index={i} setting={setting} />
                ))}
                {newSettings.map((_, i) => (
                  <ApplicationSettingsListEditRow
                    index={applicationSettings.data.length + i}
                  />
                ))}
                <div className="py-4 flex justify-center">
                  <Button
                    onClick={() => setNewSettings((ns) => [...ns, ""])}
                    size="sm"
                    className="-my-[1px] flex items-center space-x-2"
                  >
                    <PlusIcon className="h-4 w-4 -ml-1" />
                    <p>Add Setting</p>
                  </Button>
                </div>
                <div className="pt-4 flex items-center space-x-4">
                  <Button className="flex items-center space-x-2" type="submit">
                    <PlusIcon className="h-5 w-5 -ml-1" />
                    <p>Save</p>
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => onComplete(false)}
                    className="flex items-center space-x-2"
                  >
                    <XMarkIcon className="h-5 w-5 -ml-1" />
                    <p>Cancel</p>
                  </Button>
                </div>
              </div>
            </>
          )}
        </Form>
      </>
    );
  } else {
    return (
      <div className="divide-y divide-gray-100">
        {applicationSettings.data?.length > 0 ? (
          applicationSettings.data?.map((setting, i) => (
            <ApplicationSettingsListRow
              index={i}
              setting={setting}
              initialShow={showAll}
              onDelete={onDelete}
            />
          ))
        ) : (
          <div className="py-10 flex justify-center">
            <span className="text-sm font-medium text-gray-400">
              No application settings
            </span>
          </div>
        )}
      </div>
    );
  }
}
